/**
 * Which operators each filter field offers, how they read, and the value shape
 * each operator takes. The backend accepts the same ids (and still the legacy
 * ones: is / is_not on text, gt / lt, a string for a list operator), and
 * `upgradeLegacyFilters` here mirrors its `normalize_saved_filters` command so a
 * view stored in the old shape opens exactly as the command would rewrite it.
 */

export const OPERATORS = {
  IS_ANY_OF: "is_one_of",
  IS_NONE_OF: "is_none_of",
  CONTAINS: "contains",
  NOT_CONTAINS: "not_contains",
  STARTS_WITH: "starts_with",
  AT_LEAST: "gte",
  AT_MOST: "lte",
  BETWEEN: "between",
  IS: "is",
  IS_NOT: "is_not",
  NOT_EMPTY: "exists",
  EMPTY: "not_exists",
  // Legacy strict comparisons, still accepted by the backend.
  GT: "gt",
  LT: "lt",
  IS_BEFORE: "is_before",
  IS_AFTER: "is_after",
  IS_TODAY: "is_today",
  IS_BEFORE_TODAY: "is_before_today",
  IN_LAST_3_DAYS: "in_last_3_days",
  IN_LAST_7_DAYS: "in_last_7_days",
  IN_LAST_30_DAYS: "in_last_30_days",
  IN_LAST_3_MONTHS: "in_last_3_months",
  MORE_THAN_7_DAYS_AGO: "more_than_7_days_ago",
  MORE_THAN_A_MONTH_AGO: "more_than_a_month_ago",
}

const O = OPERATORS

export const OPERATOR_LABELS = {
  [O.IS_ANY_OF]: "is any of",
  [O.IS_NONE_OF]: "is none of",
  [O.CONTAINS]: "contains any of",
  [O.NOT_CONTAINS]: "contains none of",
  [O.STARTS_WITH]: "starts with",
  [O.AT_LEAST]: "at least",
  [O.AT_MOST]: "at most",
  [O.BETWEEN]: "between",
  [O.IS]: "is",
  [O.IS_NOT]: "is not",
  [O.NOT_EMPTY]: "is not empty",
  [O.EMPTY]: "is empty",
  [O.GT]: "more than",
  [O.LT]: "less than",
  [O.IS_BEFORE]: "before",
  [O.IS_AFTER]: "after",
  [O.IS_TODAY]: "today",
  [O.IS_BEFORE_TODAY]: "before today",
  [O.IN_LAST_3_DAYS]: "in the last 3 days",
  [O.IN_LAST_7_DAYS]: "in the last 7 days",
  [O.IN_LAST_30_DAYS]: "in the last 30 days",
  [O.IN_LAST_3_MONTHS]: "in the last 3 months",
  [O.MORE_THAN_7_DAYS_AGO]: "more than 7 days ago",
  [O.MORE_THAN_A_MONTH_AGO]: "more than a month ago",
}

const LIST_OPERATORS = new Set([
  O.IS_ANY_OF,
  O.IS_NONE_OF,
  O.CONTAINS,
  O.NOT_CONTAINS,
  O.STARTS_WITH,
])
const VALUELESS_OPERATORS = new Set([
  O.NOT_EMPTY,
  O.EMPTY,
  O.IS_TODAY,
  O.IS_BEFORE_TODAY,
  O.IN_LAST_3_DAYS,
  O.IN_LAST_7_DAYS,
  O.IN_LAST_30_DAYS,
  O.IN_LAST_3_MONTHS,
  O.MORE_THAN_7_DAYS_AGO,
  O.MORE_THAN_A_MONTH_AGO,
])

export const VALUE_SHAPES = {
  LIST: "list",
  SINGLE: "single",
  RANGE: "range",
  NONE: "none",
}

export function operatorValueShape(type) {
  if (!type) {
    return VALUE_SHAPES.NONE
  }
  if (LIST_OPERATORS.has(type)) {
    return VALUE_SHAPES.LIST
  }
  if (VALUELESS_OPERATORS.has(type)) {
    return VALUE_SHAPES.NONE
  }
  if (type === O.BETWEEN) {
    return VALUE_SHAPES.RANGE
  }
  return VALUE_SHAPES.SINGLE
}

export const FIELD_KINDS = {
  // Free text matched by "contains": Name, Company, Email, Phone.
  TEXT: "text",
  // Typed values matched whole: City, State, ZIP, MLS, Office, Upload.
  LIST: "list",
  // Fixed options picked from a menu: Role, Potential, Stage, Source...
  CHOICE: "choice",
  NUMBER: "number",
  DATE: "date",
  BOOLEAN: "boolean",
  TAG: "tag",
  // Rows the dialog only describes (Bulk search).
  SPECIAL: "special",
}

const K = FIELD_KINDS

const DATE_PRESETS = [
  O.IS_TODAY,
  O.IN_LAST_3_DAYS,
  O.IN_LAST_7_DAYS,
  O.IN_LAST_30_DAYS,
  O.IN_LAST_3_MONTHS,
  O.IS_BEFORE_TODAY,
  O.MORE_THAN_7_DAYS_AGO,
  O.MORE_THAN_A_MONTH_AGO,
]
const DATE_OPERATORS = [
  O.IS,
  O.IS_BEFORE,
  O.IS_AFTER,
  O.BETWEEN,
  ...DATE_PRESETS,
]
const DATE_OPERATOR_LABELS = { [O.IS]: "on" }
const TEXT_OPERATORS = [O.CONTAINS, O.NOT_CONTAINS, O.EMPTY, O.NOT_EMPTY]
const RANGE_OPERATORS = [O.AT_LEAST, O.AT_MOST, O.BETWEEN]
const PICK_OPERATORS = [O.IS_ANY_OF, O.IS_NONE_OF]

/**
 * Every filter field of My MLS, Uploaded leads and Candidates. `operators` is
 * the order the menu shows them in; `money` numbers get $ and separators;
 * `whole` numbers are counts (a legacy "> 5" means "at least 6").
 */
export const FIELD_SPECS = {
  name: {
    label: "Name",
    kind: K.TEXT,
    operators: [O.CONTAINS, O.NOT_CONTAINS],
  },
  candidate_name: {
    label: "Name",
    kind: K.TEXT,
    operators: [O.CONTAINS, O.NOT_CONTAINS],
  },
  company_name: { label: "Company", kind: K.TEXT, operators: TEXT_OPERATORS },
  email: { label: "Email", kind: K.TEXT, operators: TEXT_OPERATORS },
  phone: { label: "Phone", kind: K.TEXT, operators: TEXT_OPERATORS },
  mls: { label: "MLS", kind: K.LIST, operators: PICK_OPERATORS },
  city: {
    label: "City",
    kind: K.LIST,
    operators: [...PICK_OPERATORS, O.CONTAINS, O.NOT_CONTAINS],
  },
  state: { label: "State", kind: K.LIST, operators: PICK_OPERATORS },
  zipcode: {
    label: "ZIP code",
    kind: K.LIST,
    operators: [...PICK_OPERATORS, O.STARTS_WITH],
  },
  office_name: {
    label: "Office",
    kind: K.LIST,
    operators: [...PICK_OPERATORS, O.CONTAINS, O.NOT_CONTAINS],
  },
  upload_name: { label: "Upload", kind: K.LIST, operators: PICK_OPERATORS },
  role: { label: "Role", kind: K.CHOICE, operators: PICK_OPERATORS },
  potential_tier: {
    label: "Potential",
    kind: K.CHOICE,
    operators: PICK_OPERATORS,
  },
  crm_stage_id: { label: "Stage", kind: K.CHOICE, operators: PICK_OPERATORS },
  type: { label: "Type", kind: K.CHOICE, operators: PICK_OPERATORS },
  concierge_last_disposition: {
    label: "Last call outcome",
    kind: K.CHOICE,
    operators: PICK_OPERATORS,
  },
  call_performed_by: {
    label: "Called by",
    kind: K.CHOICE,
    operators: PICK_OPERATORS,
  },
  annual_value: {
    label: "Annual value",
    kind: K.NUMBER,
    money: true,
    operators: RANGE_OPERATORS,
  },
  total_production: {
    label: "Sales volume, last 12 months",
    kind: K.NUMBER,
    money: true,
    operators: RANGE_OPERATORS,
  },
  total_transactions_count: {
    label: "Sales, last 12 months",
    kind: K.NUMBER,
    whole: true,
    operators: RANGE_OPERATORS,
  },
  all_time_production: {
    label: "Sales volume, all time",
    kind: K.NUMBER,
    money: true,
    operators: RANGE_OPERATORS,
  },
  all_time_transactions_count: {
    label: "Sales, all time",
    kind: K.NUMBER,
    whole: true,
    operators: RANGE_OPERATORS,
  },
  years_in_business: {
    label: "Years in business",
    kind: K.NUMBER,
    whole: true,
    operators: [...RANGE_OPERATORS, O.EMPTY],
    operatorLabels: { [O.EMPTY]: "is unknown" },
  },
  rating: {
    label: "Rating",
    kind: K.NUMBER,
    whole: true,
    operators: [O.IS, ...RANGE_OPERATORS, O.EMPTY],
    hint: "From 0 to 10",
  },
  concierge_dial_count: {
    label: "Number of dials",
    kind: K.NUMBER,
    whole: true,
    operators: [O.IS, O.AT_LEAST, O.AT_MOST],
  },
  likelihood_to_move: {
    label: "Likelihood to move",
    kind: K.NUMBER,
    operators: RANGE_OPERATORS,
  },
  custom_tag: {
    label: "Tag",
    kind: K.TAG,
    operators: [O.CONTAINS, O.NOT_CONTAINS, O.NOT_EMPTY, O.EMPTY],
    operatorLabels: {
      [O.CONTAINS]: "has any of",
      [O.NOT_CONTAINS]: "has none of",
      [O.NOT_EMPTY]: "has any tag",
      [O.EMPTY]: "has no tag",
    },
  },
  is_dnc: { label: "On DNC list", kind: K.BOOLEAN, operators: [O.IS] },
  // The same numbers the Power Dialer calls (backend crm/dialable_phone.py).
  has_callable_phone: {
    label: "Has a callable phone",
    kind: K.BOOLEAN,
    operators: [O.IS],
  },
  bulk_list: { label: "Bulk search list", kind: K.SPECIAL, operators: [O.IS] },
  concierge_last_dialed_at: {
    label: "Last call",
    kind: K.DATE,
    operators: [...DATE_OPERATORS, O.EMPTY, O.NOT_EMPTY],
    operatorLabels: {
      ...DATE_OPERATOR_LABELS,
      [O.EMPTY]: "has never been called",
      [O.NOT_EMPTY]: "has been called",
    },
  },
  stamp: {
    label: "Date added",
    kind: K.DATE,
    operators: DATE_OPERATORS,
    operatorLabels: DATE_OPERATOR_LABELS,
  },
}

// Uploaded leads compare the numbers from the uploaded file, not a rolling year.
const PAGE_LABEL_OVERRIDES = {
  uploadedleads: {
    total_production: "Sales volume",
    total_transactions_count: "Sales",
  },
}

export function fieldSpec(fieldName) {
  return FIELD_SPECS[fieldName] || null
}

export function fieldLabel(fieldName, page) {
  return (
    PAGE_LABEL_OVERRIDES[page]?.[fieldName] ||
    FIELD_SPECS[fieldName]?.label ||
    fieldName
  )
}

export function operatorLabel(fieldName, type) {
  return (
    FIELD_SPECS[fieldName]?.operatorLabels?.[type] ||
    OPERATOR_LABELS[type] ||
    type
  )
}

/**
 * The operators the menu offers for a field. A saved filter can still hold an
 * operator the field no longer offers (ZIP "contains"); it stays listed for that
 * row so the filter reads correctly and keeps working.
 */
export function operatorsForField(fieldName, currentType) {
  const operators = FIELD_SPECS[fieldName]?.operators || []
  if (currentType && !operators.includes(currentType)) {
    return [...operators, currentType]
  }
  return operators
}

const SEPARATOR = /[,;\n\r]+/

/**
 * The comma rule: a comma, semicolon or new line always starts a new value.
 * Spaces around values are removed, empties dropped, and repeats (ignoring
 * case) kept once. Numbers pass through untouched.
 */
export function splitValues(value) {
  const items = Array.isArray(value)
    ? value.flatMap((item) =>
        typeof item === "string" ? item.split(SEPARATOR) : [item]
      )
    : typeof value === "string"
    ? value.split(SEPARATOR)
    : value == null
    ? []
    : [value]
  return dedupe(items)
}

// Like splitValues, but a string item is kept whole: "is" never split its value,
// so "Keller Williams Realty, Inc." must stay one value when upgraded.
function cleanList(value) {
  const items = Array.isArray(value) ? value : value == null ? [] : [value]
  return dedupe(items)
}

function dedupe(items) {
  const seen = new Set()
  const result = []
  for (const raw of items) {
    if (raw == null) {
      continue
    }
    const item = typeof raw === "string" ? raw.trim() : raw
    if (item === "") {
      continue
    }
    const key = typeof item === "string" ? item.toLowerCase() : String(item)
    if (seen.has(key)) {
      continue
    }
    seen.add(key)
    result.push(item)
  }
  return result
}

/** Drop the dialog's own keys (`_id`, `_complete`) and the retired `exact`. */
export function sanitizeFilter(filter) {
  return Object.fromEntries(
    Object.entries(filter || {}).filter(
      ([key, value]) =>
        !key.startsWith("_") && key !== "exact" && value !== undefined
    )
  )
}

export function sanitizeFilters(filters) {
  return (filters || []).map(sanitizeFilter)
}

function toNumber(value) {
  if (value === "" || value == null || typeof value === "boolean") {
    return null
  }
  const n = Number(
    typeof value === "string" ? value.replace(/[$,\s]/g, "") : value
  )
  return Number.isFinite(n) ? n : null
}

function toNumberForField(value, spec) {
  const n = toNumber(value)
  if (n == null) {
    return null
  }
  return spec?.whole ? Math.trunc(n) : n
}

function toBoolean(value) {
  if (typeof value === "boolean") {
    return value
  }
  return ["true", "1", "yes", "dnc"].includes(
    String(value ?? "")
      .trim()
      .toLowerCase()
  )
}

const LEGACY_FIELD_NAMES = {
  total_dollar_ltm: "total_production",
  sales_count: "total_transactions_count",
}

function withValue(filter, type, value) {
  const { value: _ignored, ...rest } = filter
  if (operatorValueShape(type) === VALUE_SHAPES.NONE) {
    return { ...rest, type }
  }
  return { ...rest, type, value }
}

function upgradeRange(value, toValue) {
  const [low, high] = Array.isArray(value) ? value : [value, null]
  const range = [toValue(low), toValue(high)]
  return range.every((item) => item == null) ? null : range
}

/**
 * One stored filter in the shape the new dialog builds, or null when it filters
 * nothing (empty value). Idempotent: an upgraded filter upgrades to itself.
 */
export function upgradeLegacyFilter(rawFilter, page) {
  let filter = sanitizeFilter(rawFilter)
  if (!filter.field || !filter.type) {
    return null
  }
  if (LEGACY_FIELD_NAMES[filter.field]) {
    filter = { ...filter, field: LEGACY_FIELD_NAMES[filter.field] }
  }
  if (filter.field === "stamp_range") {
    const range = upgradeRange(filter.value, (item) => item || null)
    return range
      ? { ...filter, field: "stamp", type: O.BETWEEN, value: range }
      : null
  }

  const spec = FIELD_SPECS[filter.field]
  const kind = spec?.kind
  let type = filter.type
  const value = filter.value

  if (kind === K.SPECIAL) {
    return filter
  }

  if (kind === K.BOOLEAN) {
    if (value == null || value === "") {
      return null
    }
    const flips = type === O.IS_NOT || type === O.NOT_CONTAINS
    const bool = toBoolean(value)
    return { ...filter, type: O.IS, value: flips ? !bool : bool }
  }

  if (kind === K.NUMBER) {
    if (type === O.GT || type === O.LT) {
      const n = toNumberForField(value, spec)
      if (n == null) {
        return null
      }
      if (type === O.GT) {
        return { ...filter, type: O.AT_LEAST, value: spec.whole ? n + 1 : n }
      }
      return { ...filter, type: O.AT_MOST, value: spec.whole ? n - 1 : n }
    }
    if (type === O.BETWEEN) {
      const range = upgradeRange(value, (item) => toNumberForField(item, spec))
      return range ? { ...filter, value: range } : null
    }
    if (operatorValueShape(type) === VALUE_SHAPES.NONE) {
      return withValue(filter, type)
    }
    const n = toNumberForField(value, spec)
    if (n == null) {
      // Rating "is (empty)" meant "has no rating" (view 292): keep that.
      return type === O.IS ? withValue(filter, O.EMPTY) : null
    }
    return { ...filter, value: n }
  }

  if (kind === K.DATE) {
    if (type === O.GT) {
      type = O.IS_AFTER
    } else if (type === O.LT) {
      type = O.IS_BEFORE
    }
    const shape = operatorValueShape(type)
    if (shape === VALUE_SHAPES.NONE) {
      return withValue(filter, type)
    }
    if (shape === VALUE_SHAPES.RANGE) {
      const range = upgradeRange(value, (item) => item || null)
      return range ? { ...filter, type, value: range } : null
    }
    return value ? { ...filter, type, value } : null
  }

  const listKind =
    kind === K.TEXT || kind === K.LIST || kind === K.CHOICE || kind === K.TAG
  if (operatorValueShape(type) === VALUE_SHAPES.NONE) {
    return withValue(filter, type)
  }
  if (type === O.IS || type === O.IS_NOT) {
    // Single-value "is" on a text or choice field is "is any of" with one value.
    // Unknown fields (boost criteria's brand, county) only switch when they
    // already hold a list.
    if (listKind || Array.isArray(value)) {
      const list = cleanList(value)
      if (!list.length) {
        return null
      }
      type = type === O.IS ? O.IS_ANY_OF : O.IS_NONE_OF
      filter = { ...filter, value: list }
    } else {
      return value == null || value === ""
        ? null
        : {
            ...filter,
            value: typeof value === "string" ? value.trim() : value,
          }
    }
  } else if (LIST_OPERATORS.has(type)) {
    // A typed string splits by the comma rule; a stored list is already split
    // (one chip may hold "Keller Williams Realty, Inc.").
    const list = Array.isArray(value) ? cleanList(value) : splitValues(value)
    if (!list.length) {
      return null
    }
    filter = { ...filter, value: list }
  }
  if (filter.field === "phone") {
    // Phone formatting is only cleaned for "contains"; a list never matched.
    if (type === O.IS_ANY_OF) {
      type = O.CONTAINS
    } else if (type === O.IS_NONE_OF) {
      type = O.NOT_CONTAINS
    }
  }
  return { ...filter, type }
}

export function upgradeLegacyFilters(filters, page) {
  if (!Array.isArray(filters)) {
    return []
  }
  return filters
    .map((filter) => upgradeLegacyFilter(filter, page))
    .filter(Boolean)
}

/**
 * The value a row keeps when its operator changes, so a typed value is never
 * left in a shape the new operator can't read (a string under "is any of" was
 * matched letter by letter).
 */
export function convertValueForOperator(fieldName, fromType, toType, value) {
  const spec = FIELD_SPECS[fieldName]
  const kind = spec?.kind
  const toShape = operatorValueShape(toType)
  const fromShape = operatorValueShape(fromType)

  if (kind === K.BOOLEAN) {
    return typeof value === "boolean" ? value : undefined
  }
  if (toShape === VALUE_SHAPES.NONE) {
    return undefined
  }

  const items =
    fromShape === VALUE_SHAPES.NONE
      ? []
      : fromShape === VALUE_SHAPES.SINGLE
      ? kind === K.NUMBER || kind === K.DATE
        ? cleanList(value)
        : splitValues(value)
      : cleanList(value)

  if (toShape === VALUE_SHAPES.LIST) {
    if (kind === K.NUMBER || kind === K.DATE) {
      return []
    }
    return items
  }

  const coerce =
    kind === K.NUMBER
      ? (item) => toNumberForField(item, spec)
      : (item) => (item === "" || item == null ? null : item)

  if (toShape === VALUE_SHAPES.RANGE) {
    if (fromShape === VALUE_SHAPES.RANGE) {
      return value
    }
    const first = items.length ? coerce(items[0]) : null
    return [first, null]
  }

  // Single value.
  if (fromShape === VALUE_SHAPES.SINGLE) {
    return value
  }
  if (fromShape === VALUE_SHAPES.RANGE) {
    const first = items.map(coerce).find((item) => item != null)
    return first ?? undefined
  }
  if (kind === K.NUMBER || kind === K.DATE) {
    return items.length === 1 ? coerce(items[0]) ?? undefined : undefined
  }
  return items.length ? items.join(", ") : undefined
}

/** Whether a dialog row is complete enough to filter by. */
export function isFilterComplete(filter) {
  if (!filter?.field || !filter.type) {
    return false
  }
  const kind = FIELD_SPECS[filter.field]?.kind
  if (kind === K.BOOLEAN) {
    return typeof filter.value === "boolean"
  }
  switch (operatorValueShape(filter.type)) {
    case VALUE_SHAPES.NONE:
      return true
    case VALUE_SHAPES.LIST:
      return Array.isArray(filter.value) && filter.value.length > 0
    case VALUE_SHAPES.RANGE:
      return (
        Array.isArray(filter.value) &&
        filter.value.some((item) => item != null && item !== "")
      )
    default:
      if (kind === K.NUMBER) {
        return toNumber(filter.value) != null
      }
      return filter.value != null && filter.value !== ""
  }
}
